import Keycloak from 'keycloak-js';
import { Session } from '../models/Session';
import { Enrollment } from '../components/enrollment/models/Enrollment';
import { DisorderInformation } from '../components/enrollment/models/DisorderInformation';

class ApiService {
  private keycloak: Keycloak;
  private baseUrl: string = document.location.origin;

  constructor(keycloak: Keycloak) {
    this.keycloak = keycloak;
  }

  private get authHeader(): string {
    const token = this.keycloak.token;
    if (!token) {
      throw new Error('No authentication token available.');
    }

    return `Bearer ${token}`;
  }

  private async refreshTokenOrLogin(): Promise<boolean> {
    try {
      await this.keycloak.updateToken();
      if (this.keycloak.token) {
        return true;
      }
      this.keycloak.login();
      return false;
    } catch {
      this.keycloak.login();
      return false;
    }
  }

  async createAcousticTask(acousticTask: ApiAcousticTask): Promise<ApiAcousticTask> {
    return await this.post(`/api/acousticTasks`, acousticTask);
  }

  async createRecordings(recordings: ApiRecording[]): Promise<void> {
    return await this.post(`/api/recordings`, recordings);
  }

  async uploadFile(file: ApiFile, site: string): Promise<string> {
    const formData = new FormData();
    const blob = await (await fetch(file.uri)).blob();
    formData.append('File', blob, file.name);
    formData.append('Site', site);

    const response = await this.postFormData(`/api/files`, formData)
    if (!response.ok) {
      throw new Error('File upload failed');
    }

    return await response.text();
  }

  async getParticipantDetails(participantId: string): Promise<ApiParticipant> {
    const participantWithDetails: ApiParticipant = await this.get(`/api/subjects/${participantId}/sessionWithData`);
    const session = participantWithDetails.sessionWithData;

    // map ApiAcousticTask to AcousticTask
    if (session && session?.acousticTasks?.length > 0) {
      session.acousticTasks = session.acousticTasks.map(mapToAcousticTask);
    }

    return participantWithDetails;
  }

  async getParticipantConsentAndLanguage(participantId: string, signal?: AbortSignal): Promise<ApiParticipantConsentInfo> {
    return await this.get(`/api/subjects/${participantId}/consentInfo`, signal);
  }

  async getEconsentSurveyLinks(id: string): Promise<EConsentResponse> {
    return await this.get(`/api/econsentSurveys/${id}`);
  }

  async createSession(session: Session): Promise<Session> {
    return await this.post(`/api/sessions`, session);
  }

  async completeSession(session: Session): Promise<Session> {
    return await this.post(`/api/sessions`, session);
  }

  async createQuestionnaire(endpoint: string, questionnaire: any): Promise<any> {
    console.log('post', endpoint);
    return await this.post(endpoint, questionnaire);
  }

  async addProlificId(participantId: string, prolificInformationPost: ProlificInformationPost): Promise<void> {
    return await this.post(`/api/subjects/${participantId}/addProlificId`, prolificInformationPost);
  }

  async getProlificStudyId(participantId: string): Promise<string> {
    const response = await this.get(`/api/subjects/${participantId}/prolificStudyId`);
    return response?.prolificStudyId ?? '';
  }

  async enrollmentCreate(enrollment: Enrollment): Promise<any> {
    try {
      const url = new URL('/api/enrollments/anonymous', this.baseUrl);
      const options: RequestInit = {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(enrollment)
      };

      return await this.fetchWithRetry(url, options);
    } catch (error) {
      console.error('An error occurred:', error);
      throw error;
    }
  }

  // private functions
  private async get(endpoint: string, signal?: AbortSignal): Promise<any> {
    console.log('get', endpoint);
    const options: RequestInit = {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
      signal
    };

    const url = new URL(endpoint, this.baseUrl);
    return await this.fetchWithRetry(url, options, { requiresAuth: true });
  }

  private async post(endpoint: string, data: object): Promise<any> {
    console.log('post', endpoint, data);

    const options: RequestInit = {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    };

    const url = new URL(endpoint, this.baseUrl);
    return await this.fetchWithRetry(url, options, { requiresAuth: true });
  }

  private async postFormData(endpoint: string, fromData: FormData): Promise<any> {
    console.log('postForm', endpoint, fromData);

    const url = new URL(endpoint, this.baseUrl);

    try {
      const options: RequestInit = {
        method: 'POST',
        headers: {
          'Authorization': this.authHeader
        },
        body: fromData
      };

      let response = await fetch(url, options);

      if (response.status === 401) {
        console.warn('Token expired during file upload, attempting refresh...');
        const refreshed = await this.refreshTokenOrLogin();
        if (!refreshed) {
          throw new Error('Unauthorized, please try logging out and back in again');
        }
        response = await fetch(url, { ...options, headers: { 'Authorization': this.authHeader } });
      }

      if (response.status === 401) {
        console.error('Unauthorized');
        throw new Error('Unauthorized, please try logging out and back in again');
      }

      if (response.status === 403) {
        console.error('Forbidden');
        throw new Error('Forbidden, you do not have permissions to access this resource');
      }
      if (response.status === 500) {
        console.error('Internal Server Error');
        throw new Error('Internal Server Error, please try again later');
      }

      if (!response.ok) {
        console.error(`Error: ${response.status} ${response.statusText}`);
        throw new Error(`HTTP error! ${await response.text()}`);
      }

      return await response;
    } catch (error) {
      console.error(`Error: ${(error as Error).message}`);
      throw error;
    }
  }

  private async fetchWithRetry(url: URL, options: RequestInit, { maxRetries = 3, delay = 1000, requiresAuth = false }: { maxRetries?: number, delay?: number, requiresAuth?: boolean } = {}): Promise<any> {
    if (requiresAuth) {
      try {
        // Verify a token exists before entering the retry loop.
        // If missing, attempt a refresh; on failure, redirect to login.
        const _ = this.authHeader;
      } catch {
        const refreshed = await this.refreshTokenOrLogin();
        if (!refreshed) {
          throw new Error('Unauthorized, please try logging out and back in again');
        }
      }
    }

    for (let attempts = 0; attempts < maxRetries; attempts++) {
      try {
        const requestOptions: RequestInit = requiresAuth
          ? { ...options, headers: { ...(options.headers as Record<string, string>), 'Authorization': this.authHeader } }
          : options;
        const response = await fetch(url, requestOptions);

        if (response.status === 204 || response.status === 404) {
          console.info(`Resource not found: ${url}`);
          return null; // Return null for 204 and 404 errors
        }

        if (response.status === 401) {
          console.warn(`Token expired on attempt ${attempts + 1}, attempting refresh...`);
          const refreshed = await this.refreshTokenOrLogin();
          if (!refreshed) {
            throw new Error('Unauthorized, please try logging out and back in again');
          }
          continue; // Retry with the refreshed token
        }

        if (response.status === 403) {
          console.error('Forbidden');
          throw new Error('Forbidden, you do not have permissions to access this resource');
        }

        if (response.status === 500) {
          console.error('Internal Server Error');
          throw new Error('Internal Server Error, please try again later');
        }

        if (!response.ok) {
          console.error(`Error: ${response.status} ${response.statusText}`);
          throw new Error(`HTTP error! ${await response.text()}`);
        }

        return await response.json();

      } catch (error) {
        console.error(`Attempt ${attempts + 1}: ${(error as Error).message}`);
        if (attempts + 1 >= maxRetries) {
          throw new Error(`Failed after ${maxRetries} attempts: ${(error as Error).message}`);
        }
        await this.delay(delay * (attempts + 1));
      }
    }
  }

  private delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }
}

export default ApiService;

export interface Terms {
  version: string;
  text: string;
  isActive: boolean;
}

export interface ApiFile {
  uri: string;
  name: string;
  type: string;
}

export interface ApiRecording {
  id: string;
  acousticTaskId: string;
  subjectId: string;
  sessionId: string;
  name: string;
  filePath: string;
  createdAt: string;
  duration: number | null;
  size: number | null;
  profileName: string | null;
  profile: string;
  inputGain: string | null;
  microphone: string | null;
  platform: string | null;
  platformVersion: string | null;
  platformArchitecture: string | null;
  browser: string | null;
  browserVersion: string | null;
  screenResolution: string | null;
  windowResolution: string | null;
  deviceInfo: string | null;
  deviceType: 'mobile' | 'likely mobile' | 'tablet' | 'likely tablet' | 'desktop' | null;
  touchScreen: 'yes' | 'no' | 'notCertain' | null;
}

export interface ApiAcousticTask {
  id: string;
  subjectId: string;
  sessionId: string;
  name: string;
  cohort: string;
  status: 'completed' | 'skipped' | string;
  startedAt?: string;
  completedAt?: string;
  language?: string;
}

export interface Questionnaire {
  sessionId: string;
  subjectId: string;
  startedAt?: string;
  completedAt?: string;
  language?: string;
}

export interface ApiDemographics extends Questionnaire {
  // Address
  city?: string;
  state?: string;
  zipcode?: string;
  country?: string;
  // Gender Identity
  genderIdentitySelection?: string;
  otherGenderIdentitySpecified?: string;
  specifyGenderIdentity?: string;
  // Sexual Orientation
  sexualOrientationSelection?: string;
  otherSexualOrientationSpecified?: string;
  // Race
  raceSelection: string[];
  otherRaceSpecified?: string;
  nativeAmericanRaceSelection: string[];
  asianRaceSelection: string[];
  blackRaceSelection: string[];
  pacificIslanderRaceSelection: string[];
  whiteRaceSelection: string[];
  canadianRaceSelection: string[];
  // Ethnicity
  hispanicLatinoStatus?: string;
  hispanicLatinoSelection: string[];
  // Education Level
  educationLevelSelection?: string;
  otherEducationLevelSpecified?: string;
  // Disability Information
  hearing?: string;
  vision?: string;
  cognition?: string;
  mobility?: string;
  selfCare?: string;
  independentLiving?: string;
  // Employment Status
  employmentStatusSelection: string[];
  otherEmploymentStatusSpecified?: string;
  // Occupation
  occupation?: string;
  // Veteran
  isVeteran: string;
  // US
  householdIncomeUsa?: string;
  financialAssistanceUsa?: string;
  financialAssistanceSelectionUsa: string[];
  // Canada
  householdIncomeCanada?: string;
  financialAssistanceCanada?: string;
  financialAssistanceSelectionCanada: string[];
  // Citizenship
  citizenSelection?: string;
  // Marital Status
  maritalStatusSelection: string[];
  // Housing Status
  housingStatusSelection?: string;
  // Household Makeup
  numberOfPeople: number;
  spousePartnerSignificantOther: string;
  children: string;
  oneOrMoreParent: string;
  oneOrMoreGrandParent: string;
  otherLiveWith: string;
  othersLiveWithSpecified: string;
  // Transportation
  reliableTransportation?: string;
  transportationSelection?: string;
  otherTransportationSpecified?: string;
}

export interface ApiVoiceQualityPerception extends Questionnaire {
  selection?: number;
}

export interface ApiVhiQuestionnaire extends Questionnaire {
  voiceDifficultHear: string;
  toughToUnderstand: string;
  voiceRestrictSocial: string;
  leftOutConversations: string;
  voiceLoseIncome: string;
  strainVoice: string;
  voiceClarity: string;
  voiceUpsetting: string;
  voiceHandicapped: string;
  askWhatsWrongVoice: string;
}

export interface ApiPatientHealthQuestionnaire extends Questionnaire {
  noInterest: string;
  feelingDepressed: string;
  troubleSleeping: string;
  noEnergy: string;
  noAppetite: string;
  feelingBadSelf: string;
  troubleConcentrate: string;
  moveSpeakSlow: string;
  thoughtsDeath: string;
  selection?: string;
}

export interface ApiGadAnxietyQuestionnaire extends Questionnaire {
  nervousAnxious: string;
  cantControlWorry: string;
  worryTooMuch: string;
  troubleRelaxing: string;
  hardToSitStill: string;
  easilyAgitated: string;
  afraidOfThings: string;
  selection?: string;
}

export interface ApiPanasQuestionnaire extends Questionnaire {
  upset: string;
  hostile: string;
  alert: string;
  ashamed: string;
  inspired: string;
  nervous: string;
  determined: string;
  attentive: string;
  afraid: string;
  active: string;
}

export interface ApiCustomAffectScaleQuestionnaire extends Questionnaire {
  sadOrDown: string;
  joyful: string;
  motivated: string;
  lackOfPleasure: string;
  tired: string;
  energetic: string;
  agitated: string;
  irritated: string;
  lonely: string;
  concentrated: string;
  relaxed: string;
  worried: string;
  desireToEscape: string;
}

export interface ApiDsm5AdultQuestionnaire extends Questionnaire {
  littleInterest: string;
  feelingDown: string;
  feelingMoreIrritated: string;
  sleepingLess: string;
  startingMoreProjects: string;
  feelingNervous: string;
  feelingPanic: string;
  avoidingSituations: string;
  unexplainedAches: string;
  illnessNotTakenSerious: string;
  selfHarm: string;
  hearingThings: string;
  someoneHearThoughts: string;
  sleepQuality: string;
  memoryIssues: string;
  unpleasantThoughts: string;
  repeatActs: string;
  feelingDetached: string;
  noPurpose: string;
  isolated: string;
  drinkingMore: string;
  smokingMore: string;
  medicationUse: string;
  mania1: string;
  mania2: string;
  mania3: string;
  mania4: string;
  mania5: string;
  socialPhobia1: string;
  socialPhobia2: string;
  socialPhobia3: string;
  socialPhobia4: string;
  socialPhobia5: string;
  socialPhobia6: string;
  socialPhobia7: string;
  socialPhobia8: string;
  socialPhobia9: string;
  socialPhobia10: string;
}

export interface ApiAdhdAdultQuestionnaire extends Questionnaire {
  wrappingUp: string;
  thingsInOrder: string;
  rememberingAppt: string;
  avoidance: string;
  fidget: string;
  overlyActive: string;
  careless: string;
  difficultyAttention: string;
  difficultyConcentrating: string;
  misplace: string;
  distracted: string;
  remainSeated: string;
  restless: string;
  difficultyUnwinding: string;
  talkingTooMuch: string;
  finishOtherSent: string;
  difficultyWaiting: string;
  interruptOthers: string;
}

export interface ApiDyspneaIndexQuestionnaire extends Questionnaire {
  airIn: string;
  tightnessThroat: string;
  effortBreathe: string;
  weatherChanges: string;
  breathingWorseStress: string;
  soundBreathingIn: string;
  strain: string;
  exercise: string;
  breathingStressesMe: string;
  restrictPersonalSocialLife: string;
}

export interface ApiLeicesterCoughQuestionnaire extends Questionnaire {
  chestStomachPains: string;
  sputumPhlegm: string;
  tired: string;
  embarrassed: string;
  anxious: string;
  interfereJob: string;
  interfereLife: string;
  exposurePaint: string;
  sleep: string;
  bout: string;
  frustrated: string;
  fedUp: string;
  hoarseVoice: string;
  seriousIllness: string;
  otherPeople: string;
  interruptConversation: string;
  partner: string;
  feltInControl: string;
  energy: string;
}

export interface ApiParticipantConsentInfo {
  id: string;
  consentStatus: ConsentStatus;
  language: string;
  isProlific?: string | null;
  prolificId?: string | null;
  showProlificStudyId?: string | null;
}

export interface ApiParticipant {
  id: string;
  dateOfBirth?: string;
  email?: string;
  firstName?: string;
  lastName?: string;
  phoneNumber?: string;
  site: string;
  consentStatus: ConsentStatus;
  isControlParticipant: boolean;
  disorderInformation: DisorderInformation;
  eligibleStudies: EligibleStudy[];
  language: string;
  sessionWithData?: ApiSessionWithData;
}

export interface ApiSessionWithData {
  id: string;
  subjectId: string;
  startedAt: Date | string;
  completedAt?: Date | string;
  completedBy: string[];
  assignedTasks: string[];
  isControlParticipant: boolean;
  language?: string;
  acousticTasks: ApiAcousticTask[];
  adhdAdultQuestionnaire?: any;
  confounders?: any;
  customAffectScaleQuestionnaire?: any;
  demographics?: any;
  dsm5AdultQuestionnaire?: any;
  dyspneaIndexQuestionnaire?: any;
  gadAnxietyQuestionnaire?: any;
  leicesterCoughQuestionnaire?: any;
  panasQuestionnaire?: any;
  patientHealthQuestionnaire?: any;
  status: '1' | '2' | string;
  vhiQuestionnaire?: any;
  voiceQualityPerception?: any;
}
export type ConsentStatus = 'Pending' | 'Consented' | 'Withdrawn Consent';
export enum EligibleStudy {
  VoiceDisorders = 'Voice Disorders',
  NeurologicalAndNeurodegenerativeDisorders = 'Neurological and Neurodegenerative Disorders',
  MoodAndPsychiatricDisorders = 'Mood and Psychiatric Disorders',
  RespiratoryDisorders = 'Respiratory Disorders'
}

const mapToAcousticTask = (apiAcousticTask: ApiAcousticTask): ApiAcousticTask => {
  const acousticTask: any = {
    ...apiAcousticTask,
    type: 'acoustic-task',
    status: apiAcousticTask.status === '2' ? 'completed' : 'pending',
  };
  return acousticTask;
}

export interface EConsentSurveyLink {
  name: string;
  url: string;
  completed: boolean;
}

export interface EConsentResponse {
  enabled: boolean;
  site: string;
  language: string;
  surveyLinks: EConsentSurveyLink[];
}

export interface ProlificInformationPost {
  prolificId: string;
}
